import { Badge } from '@/components/ui/badge';
import { TanstackSortableColumnHeader } from '@/components/table/SortColumnHeader';
import { Ellipsis } from '@/components/ui/ellipsis';
import { FlexRow } from '@/components/ui/flex';
import { getSourceIconUrl } from '@/utils/source-icon';
import { Text } from '@/components/ui/text';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import ApplicableIcon from '@/assets/applicable.svg?react';
import NotApplicableIcon from '@/assets/not_applicable.svg?react';
import { formatDistanceToNow } from 'date-fns';
import { ImplementationStatus, ListUpdatesResponse } from '@ditto/shared';
import { Column, ColumnDef } from '@tanstack/react-table';
import {
  Ban,
  CircleDashed,
  Compass,
  Copy,
  Gem,
  Loader2,
  Rss,
  Shield,
  Star,
} from 'lucide-react';
import { FC, useRef } from 'react';
import {
  PRIORITY_LABELS,
  SOURCE_TYPE_LABELS,
  STATUS_LABELS,
  UPDATE_TYPE_LABELS,
} from '../labels';
import { PriorityIcon } from './PriorityIcon';
import { DocumentReferenceButton } from './DocumentReferenceButton';
import { MarkdownText } from '@/shared/MarkdownText/MarkdownText';
import { MultiGeographyBadge } from '@/shared/geography/MultiGeographyBadge';
import { cn } from '@/utils/classUtils';
import { useIsOverflown } from '@/utils/overflowUtils';

const ColumnHeader = ({
  title,
  column,
}: {
  title: string;
  column: Column<ListUpdatesResponse, unknown>;
}) => {
  return (
    <TanstackSortableColumnHeader column={column}>
      <Ellipsis asChild lineClamp={1} hasTooltip>
        <Text variant="body-sm" color="primary" className="font-medium">
          {title}
        </Text>
      </Ellipsis>
    </TanstackSortableColumnHeader>
  );
};

export const PriorityDisplay = ({
  priority,
  aiImpactStatus,
}: {
  priority: string | null;
  aiImpactStatus: string | null;
}) => {
  if (aiImpactStatus === 'generating') {
    return <Loader2 className="h-4 w-4 animate-spin text-neutral-400" />;
  }

  if (priority === null) {
    return (
      <Text variant="body-sm" color="secondary">
        -
      </Text>
    );
  }

  const label = PRIORITY_LABELS[priority] ?? priority;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div>
          <PriorityIcon
            priority={priority}
            className="h-4 w-4 text-neutral-900"
          />
        </div>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
};

export const UpdateTypeBadge = ({ updateType }: { updateType: string }) => {
  const label = UPDATE_TYPE_LABELS[updateType] ?? updateType;

  switch (updateType) {
    case 'new':
      return (
        <Badge className="h-6 gap-1 border-transparent bg-green-100 text-neutral-900 shadow-none">
          <Star className="h-3 w-3" />
          {label}
        </Badge>
      );
    case 'revision':
      return (
        <Badge className="h-6 gap-1 border-transparent bg-yellow-100 text-neutral-900 shadow-none">
          <Copy className="h-3 w-3" />
          {label}
        </Badge>
      );
    case 'withdrawal':
      return (
        <Badge className="h-6 gap-1 border-transparent bg-red-100 text-neutral-900 shadow-none">
          <Ban className="h-3 w-3" />
          {label}
        </Badge>
      );
    case 'status_change':
      return (
        <Badge className="h-6 gap-1 border-transparent bg-blue-100 text-neutral-900 shadow-none">
          <span className="font-bold">»</span>
          {label}
        </Badge>
      );
    default:
      return (
        <Badge color="purple" className="gap-1">
          {label}
        </Badge>
      );
  }
};

const DOCUMENT_TYPE_CONFIG: Record<
  string,
  { icon: FC<{ className?: string }>; bgClass: string }
> = {
  regulation: { icon: Shield, bgClass: 'bg-magenta-100' },
  guidance: { icon: Compass, bgClass: 'bg-purple-100' },
  standard: { icon: Gem, bgClass: 'bg-orange-100' },
  news: { icon: Rss, bgClass: 'bg-blue-100' },
};

export const DocumentTypeBadge: FC<{ type: string }> = ({ type }) => {
  const config = DOCUMENT_TYPE_CONFIG[type];
  const label = SOURCE_TYPE_LABELS[type] ?? type;
  const TypeIcon = config?.icon;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 h-6 px-2 rounded-lg text-xs font-medium text-neutral-900 tracking-[0.18px]',
        config?.bgClass ?? 'bg-neutral-100'
      )}
    >
      {TypeIcon && <TypeIcon className="h-3 w-3" />}
      {label}
    </span>
  );
};

/** Icon-only glyph for compact filter pills (e.g. My references). */
export const DocumentTypeFilterGlyph: FC<{ type: string }> = ({ type }) => {
  const config = DOCUMENT_TYPE_CONFIG[type];
  const TypeIcon = config?.icon;
  if (!TypeIcon) {
    return (
      <span
        className={cn(
          'inline-flex items-center justify-center size-[22px] rounded-md shrink-0',
          'bg-neutral-100 text-[10px] font-medium text-neutral-600'
        )}
      >
        ?
      </span>
    );
  }
  return (
    <span
      className={cn(
        'inline-flex items-center justify-center size-[22px] rounded-md shrink-0',
        config.bgClass
      )}
    >
      <TypeIcon className="h-3 w-3" />
    </span>
  );
};

const STATUS_ICON: Record<string, React.FC<{ className?: string }>> = {
  to_review: CircleDashed,
  applicable: ApplicableIcon,
  not_applicable: NotApplicableIcon,
  out_of_scope: Ban,
};

export const StatusBadge = ({
  status,
}: {
  status: string | null | undefined;
}) => {
  if (!status) return null;

  const Icon = STATUS_ICON[status];
  if (!Icon) return null;

  const label = STATUS_LABELS[status] ?? status;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div>
          <Icon className="h-5 w-5 shrink-0 text-neutral-900" />
        </div>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
};

export const RegulatoryRegionBadge = MultiGeographyBadge;

/** Same headline chain as `UpdateCard` (newsfeed card header). */
export const getFeedCardHeaderTitle = (row: ListUpdatesResponse): string => {
  const referenceTitle =
    row.kind === 'news'
      ? (row.source?.title ?? row.update.shortSummary ?? '-')
      : (row.reference?.title ?? row.update.shortSummary ?? '-');
  return row.update.shortSummary ?? row.update.summary ?? referenceTitle;
};

const auditTableTextCellClass =
  'font-sans text-[12px] leading-[150%] tracking-[0.015em] text-neutral-900';

/** MarkdownText `size="sm"` maps to `body-sm` (14px); force descendants to match plain audit cells */
const auditMarkdownCellDescendantTypography =
  '[&_*]:text-[12px] [&_*]:leading-[150%] [&_*]:tracking-[0.015em]';

/** Audit table text blocks: `auditTableTextCellClass` uses 12px font + leading-[150%] → 18px per line */
const auditTableTextBlockInnerMaxHeightClass = 'max-h-[90px]';
/** Cell wrapper: inner max height + `py-[7.5px]` top and bottom */
const auditTableTextBlockCellMaxHeightClass = 'max-h-[105px]';

const IMPLEMENTATION_LABELS: Record<ImplementationStatus, string> = {
  no: 'No',
  in_progress: 'In progress',
  yes: 'Yes',
};

/** Audit table: shared body cell padding + vertical alignment for full-row centering */
const regulatoryAuditRowCellMeta = {
  cellClassName:
    'px-2 py-[7.5px] align-middle overflow-hidden [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
} as const;

/** Update + text columns: max cell height incl. padding; inner content clamps to 5 lines at audit body size */
const regulatoryAuditTextBlockCellMeta = {
  cellClassName: `${auditTableTextBlockCellMaxHeightClass} px-2 py-[7.5px] align-middle overflow-hidden [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]`,
} as const;

export const SourceBadge = ({
  sourceTitle,
  sourceIcon,
}: {
  sourceTitle: string;
  sourceIcon?: string | null;
}) => {
  return (
    <span className="inline-flex items-center gap-[6px] px-[8px] py-[3px] rounded-[8px] text-[12px] font-medium text-[#171717] tracking-[0.18px] bg-[#FAFAFA] border border-solid border-[#E5E5E5]">
      {sourceIcon && (
        <img
          src={sourceIcon}
          alt=""
          className="w-[16px] h-[16px] object-contain"
        />
      )}
      <Ellipsis lineClamp={1} hasTooltip>
        {sourceTitle}
      </Ellipsis>
    </span>
  );
};

type UpdatesWorkspaceMember = {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
};

const PublicationEffectiveCell: FC<{ iso: string }> = ({ iso }) => (
  <div className="flex flex-col">
    <span className="font-mono text-[11px] leading-[16.5px] text-neutral-900 whitespace-nowrap">
      {iso.substring(0, 10)}
    </span>
    <span className="text-[11px] leading-[16.5px] text-neutral-500 whitespace-nowrap">
      {formatDistanceToNow(new Date(iso), { addSuffix: true }).replace(
        /^about /,
        ''
      )}
    </span>
  </div>
);

function formatAssigneeNames(
  ids: string[],
  members: UpdatesWorkspaceMember[]
): string {
  return ids
    .map(id => {
      const m = members.find(x => x.id === id);
      if (!m) return null;
      const name = `${m.firstName ?? ''} ${m.lastName ?? ''}`.trim();
      return name || m.email;
    })
    .filter(Boolean)
    .join(', ');
}

/** Markdown audit cells: styled `MarkdownText` + trailing … when overflow clips content (no hover tooltip) */
const AuditMarkdownTruncatedCell: FC<{ text: string }> = ({ text }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isTruncated = useIsOverflown(containerRef, text);

  return (
    <div
      ref={containerRef}
      className={cn(
        auditTableTextCellClass,
        'relative min-h-0 min-w-0 w-full overflow-hidden break-normal [overflow-wrap:anywhere]',
        auditTableTextBlockInnerMaxHeightClass
      )}
    >
      <MarkdownText
        className={cn(
          auditTableTextCellClass,
          'gap-0 [&_div]:mt-0 [&_p]:mt-0 [&_ul]:my-0 [&_ol]:my-0 [&_li]:mb-0',
          auditMarkdownCellDescendantTypography
        )}
        size="sm"
      >
        {text}
      </MarkdownText>
      {isTruncated ? (
        <span
          aria-hidden
          className="pointer-events-none absolute bottom-0.5 right-0 rounded-sm bg-white/90 px-0.5 text-[12px] leading-[150%] text-neutral-900 shadow-sm"
        >
          …
        </span>
      ) : null}
    </div>
  );
};
export const useUpdatesColumns = (
  workspaceUname: string,
  members: UpdatesWorkspaceMember[]
): ColumnDef<ListUpdatesResponse>[] => {
  return [
    {
      id: 'update',
      meta: {
        ...regulatoryAuditTextBlockCellMeta,
        stickyLastLeftBorder2: true,
      },
      accessorFn: row => getFeedCardHeaderTitle(row),
      header: ({ column }) => <ColumnHeader title="Update" column={column} />,
      cell: ({ row }) => (
        <FlexRow
          alignItems="center"
          gap="2"
          className={cn(
            'min-h-0 min-w-0 w-full',
            auditTableTextBlockInnerMaxHeightClass
          )}
        >
          <PriorityDisplay
            priority={row.original.context?.priority ?? null}
            aiImpactStatus={row.original.context?.aiImpactStatus ?? null}
          />
          <StatusBadge status={row.original.context?.status} />
          <Ellipsis
            lineClamp={5}
            hasTooltip
            className={cn(
              auditTableTextCellClass,
              'm-0 font-medium min-w-0 w-full break-normal [overflow-wrap:anywhere]',
              auditTableTextBlockInnerMaxHeightClass
            )}
          >
            {getFeedCardHeaderTitle(row.original)}
          </Ellipsis>
        </FlexRow>
      ),
      size: 340,
      minSize: 200,
    },
    {
      accessorKey: 'update.publicationDate',
      meta: regulatoryAuditRowCellMeta,
      header: ({ column }) => (
        <ColumnHeader title="Published" column={column} />
      ),
      cell: ({ row }) => (
        <PublicationEffectiveCell iso={row.original.update.publicationDate} />
      ),
      size: 130,
      minSize: 130,
      maxSize: 130,
    },
    {
      accessorKey: 'update.effectiveDate',
      meta: regulatoryAuditRowCellMeta,
      header: ({ column }) => (
        <ColumnHeader title="Effective" column={column} />
      ),
      cell: ({ row }) => {
        const iso = row.original.update.effectiveDate;
        if (!iso)
          return (
            <Text
              variant="body-sm"
              color="primary"
              className="font-mono text-[11px] leading-[16.5px]"
            >
              -
            </Text>
          );
        return <PublicationEffectiveCell iso={iso} />;
      },
      size: 130,
      minSize: 130,
      maxSize: 130,
    },
    {
      accessorKey: 'reference.title',
      meta: regulatoryAuditRowCellMeta,
      header: ({ column }) => (
        <ColumnHeader title="Reference" column={column} />
      ),
      cell: ({ row }) => {
        if (row.original.kind === 'news') return null;
        const title =
          row.original.reference?.title ??
          row.original.update.shortSummary ??
          '-';
        const referenceId = row.original.reference?.id;
        return (
          <FlexRow alignItems="center" className="min-w-0">
            <DocumentReferenceButton
              workspaceUname={workspaceUname}
              referenceId={referenceId}
              title={title}
              className="max-w-full"
            />
          </FlexRow>
        );
      },
      size: 200,
      minSize: 100,
      maxSize: 400,
    },
    {
      accessorKey: 'update.updateType',
      meta: regulatoryAuditRowCellMeta,
      header: ({ column }) => (
        <ColumnHeader title="Update type" column={column} />
      ),
      cell: ({ row }) => {
        const t = row.original.update.updateType;
        if (!t || row.original.kind === 'news') return null;
        return <UpdateTypeBadge updateType={t} />;
      },
      size: 150,
      minSize: 100,
      maxSize: 300,
    },
    {
      id: 'documentType',
      meta: regulatoryAuditRowCellMeta,
      accessorFn: row =>
        row.kind === 'news' ? 'news' : (row.reference?.type ?? null),
      header: ({ column }) => (
        <ColumnHeader title="Reference type" column={column} />
      ),
      cell: ({ row }) => {
        const type =
          row.original.kind === 'news' ? 'news' : row.original.reference?.type;
        return type ? <DocumentTypeBadge type={type} /> : null;
      },
      size: 160,
      minSize: 100,
      maxSize: 280,
    },
    {
      id: 'geography',
      meta: regulatoryAuditRowCellMeta,
      accessorFn: row =>
        (row.reference?.source ?? row.source)?.geography ?? null,
      header: ({ column }) => (
        <ColumnHeader title="Regulatory region" column={column} />
      ),
      cell: ({ row }) => {
        const source = row.original.reference?.source ?? row.original.source;
        return source ? (
          <RegulatoryRegionBadge
            geography={source.geography}
            institutionGeographies={source.institutionGeographies}
            institutionName={source.institutionName}
          />
        ) : null;
      },
      size: 200,
      minSize: 100,
      maxSize: 300,
    },
    {
      accessorKey: 'reference.source.title',
      meta: regulatoryAuditRowCellMeta,
      header: ({ column }) => <ColumnHeader title="Source" column={column} />,
      cell: ({ row }) => {
        const source = row.original.reference?.source ?? row.original.source;
        if (!source) return null;
        return (
          <SourceBadge
            sourceTitle={source.title}
            sourceIcon={getSourceIconUrl(source.id, source.icon)}
          />
        );
      },
      size: 180,
      minSize: 100,
      maxSize: 300,
    },
    {
      id: 'assignedTo',
      meta: regulatoryAuditRowCellMeta,
      accessorFn: row => row.context?.assignedMemberIds?.join(',') ?? '',
      header: ({ column }) => (
        <ColumnHeader title="Assigned to" column={column} />
      ),
      cell: ({ row }) => {
        const ids = row.original.context?.assignedMemberIds ?? [];
        if (ids.length === 0) return null;
        const text = formatAssigneeNames(ids, members);
        if (!text) return null;
        return (
          <Text variant="body-sm" color="primary" className="font-normal">
            {text}
          </Text>
        );
      },
      size: 180,
      minSize: 120,
      maxSize: 280,
    },
    {
      id: 'impactAssessment',
      meta: regulatoryAuditTextBlockCellMeta,
      accessorFn: row => row.context?.impactSummary ?? '',
      header: ({ column }) => (
        <ColumnHeader title="Impact assessment" column={column} />
      ),
      cell: ({ row }) => {
        const text = row.original.context?.impactSummary;
        if (!text) return null;
        return <AuditMarkdownTruncatedCell text={text} />;
      },
      size: 450,
      minSize: 150,
    },
    {
      id: 'suggestedActions',
      meta: regulatoryAuditTextBlockCellMeta,
      accessorFn: row => row.context?.suggestedActions ?? '',
      header: ({ column }) => (
        <ColumnHeader title="Recommendations" column={column} />
      ),
      cell: ({ row }) => {
        const text = row.original.context?.suggestedActions;
        if (!text) return null;
        return <AuditMarkdownTruncatedCell text={text} />;
      },
      size: 400,
      minSize: 150,
      maxSize: 500,
    },
    {
      id: 'comment',
      meta: regulatoryAuditTextBlockCellMeta,
      accessorFn: row => row.context?.statusJustification ?? '',
      header: ({ column }) => (
        <ColumnHeader title="Justification" column={column} />
      ),
      cell: ({ row }) => {
        const text = row.original.context?.statusJustification;
        if (!text) return null;
        return (
          <FlexRow
            alignItems="center"
            className={cn(
              'min-h-0 min-w-0 w-full',
              auditTableTextBlockInnerMaxHeightClass
            )}
          >
            <Ellipsis
              lineClamp={5}
              className={cn(
                auditTableTextCellClass,
                'm-0 font-normal min-w-0 w-full break-normal [overflow-wrap:anywhere]',
                auditTableTextBlockInnerMaxHeightClass
              )}
            >
              {text}
            </Ellipsis>
          </FlexRow>
        );
      },
      size: 300,
      minSize: 150,
      maxSize: 500,
    },
    {
      id: 'actionPlan',
      meta: regulatoryAuditTextBlockCellMeta,
      accessorFn: row => row.context?.actionPlan ?? '',
      header: ({ column }) => (
        <ColumnHeader title="Action plan" column={column} />
      ),
      cell: ({ row }) => {
        if (row.original.context?.status !== 'applicable') return null;
        const text = row.original.context?.actionPlan;
        if (!text) return null;
        return (
          <FlexRow
            alignItems="center"
            className={cn(
              'min-h-0 min-w-0 w-full',
              auditTableTextBlockInnerMaxHeightClass
            )}
          >
            <Ellipsis
              lineClamp={5}
              className={cn(
                auditTableTextCellClass,
                'm-0 font-normal min-w-0 w-full break-normal [overflow-wrap:anywhere]',
                auditTableTextBlockInnerMaxHeightClass
              )}
            >
              {text}
            </Ellipsis>
          </FlexRow>
        );
      },
      size: 280,
      minSize: 150,
      maxSize: 400,
    },
    {
      id: 'implemented',
      meta: regulatoryAuditRowCellMeta,
      accessorFn: row =>
        row.context?.status === 'applicable' &&
        row.context?.implementationStatus
          ? IMPLEMENTATION_LABELS[row.context.implementationStatus]
          : '',
      header: ({ column }) => (
        <ColumnHeader title="Implemented" column={column} />
      ),
      cell: ({ row }) => {
        if (row.original.context?.status !== 'applicable') return null;
        const impl = row.original.context?.implementationStatus;
        if (!impl) return null;
        return (
          <Text variant="body-sm" color="primary" className="font-normal">
            {IMPLEMENTATION_LABELS[impl]}
          </Text>
        );
      },
      size: 140,
      minSize: 120,
      maxSize: 180,
    },
    {
      id: 'supportingEvidence',
      meta: regulatoryAuditTextBlockCellMeta,
      accessorFn: row => row.context?.supportingEvidence ?? '',
      header: ({ column }) => (
        <ColumnHeader title="Supporting evidence" column={column} />
      ),
      cell: ({ row }) => {
        if (row.original.context?.status !== 'applicable') return null;
        const text = row.original.context?.supportingEvidence;
        if (!text) return null;
        return (
          <FlexRow
            alignItems="center"
            className={cn(
              'min-h-0 min-w-0 w-full',
              auditTableTextBlockInnerMaxHeightClass
            )}
          >
            <Ellipsis
              lineClamp={5}
              className={cn(
                auditTableTextCellClass,
                'm-0 font-normal min-w-0 w-full break-normal [overflow-wrap:anywhere]',
                auditTableTextBlockInnerMaxHeightClass
              )}
            >
              {text}
            </Ellipsis>
          </FlexRow>
        );
      },
      size: 280,
      minSize: 150,
      maxSize: 400,
    },
  ];
};
